/**
 * Stops the benign "ResizeObserver loop completed with undelivered
 * notifications" runtime error.
 *
 * WHAT THE ERROR IS
 * The browser raises it when a ResizeObserver callback changes layout, which
 * resizes an observed element again inside the same delivery cycle. The spec
 * response is to skip the remaining notifications, report this error, and
 * retry on the next frame — so nothing is actually broken or lost. It is a
 * warning about timing, not a failure.
 *
 * WHY IT NEEDS HANDLING ANYWAY
 * webpack-dev-server's overlay listens for `window.onerror` and renders ANY
 * uncaught error as a full-screen red "Uncaught runtime errors" panel, which
 * blocks the dev UI. Chart.js keeps a ResizeObserver per responsive chart, and
 * this dashboard renders many charts per page, so the condition is easy to hit
 * (resizing the window, toggling a sub-tab, paging a chart axis).
 *
 * WHY WE PATCH ResizeObserver RATHER THAN SWALLOW THE ERROR
 * The dev-server client script registers its `error` listener before the app
 * bundle evaluates, and for window-targeted error events listeners run in
 * registration order. A listener installed here therefore cannot call
 * stopImmediatePropagation() early enough to hide it from the overlay. The
 * only dependable fix is to not trigger the condition: defer each callback to
 * the next animation frame, so any layout it performs happens OUTSIDE the
 * observation cycle and there is nothing left undelivered.
 *
 * TRADE-OFF
 * Resize reactions land one frame (~16ms) later than they otherwise would.
 * That is imperceptible, and it is the behaviour Chart.js's own resize
 * handling already assumes.
 */
export const installResizeObserverLoopFix = (): void => {
  if (typeof window === 'undefined') return;

  const Native = window.ResizeObserver;
  if (!Native) return;

  // Guard against double-installation (React StrictMode / HMR re-evaluation).
  const flag = '__roLoopFixInstalled';
  const w = window as unknown as Record<string, unknown>;
  if (w[flag]) return;
  w[flag] = true;

  class FramedResizeObserver extends Native {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          // The observer may have been disconnected between the observation
          // and this frame, so re-check we still have something to report.
          if (!Array.isArray(entries) || entries.length === 0) return;
          callback(entries, observer);
        });
      });
    }
  }

  window.ResizeObserver = FramedResizeObserver;
};

export default installResizeObserverLoopFix;
